/**
 * <moonphase-consultation>: the Moonphase configurator, one question per step
 * and then Review (sections/moonphase-consultation.liquid, ARCHITECTURE §5, §9).
 *
 * Reads the catalog, the variant maps, the paper/ruling blocks and the sphere
 * per metal from the inline JSON, copy from data-* attributes. The config
 * persists to localStorage; `?edit=<line key>` rebuilds it from the bag
 * instead and opens Review as an update. Otherwise the saved config is
 * restored, `?material=<code>` or `?leather=<code>` overwrites that one choice,
 * and `?composition=<code>` (the Archive's COMPOSE THIS MOONPHASE link) decodes
 * the code with composition.js against the catalog and the blocks and applies
 * every field it yields — format, material, leather, paper, ruling, note,
 * spare discs and their metal — over the restored config, then opens at Review
 * when the code is complete, else at the first step it leaves unanswered; a
 * code that does not decode is ignored, and ?composition is ignored with
 * ?edit. Prices and availability come from the variant map when the product is
 * imported and from the catalog until then; nothing is added without a
 * variant, and cart.js refuses unless ordering is enabled. The composition
 * code on Review and the cart lines of a build come from
 * assets/composition.js, shared with the paste-a-composition form.
 *
 * Metals: step II offers the catalog's offered metals only. A withdrawn metal
 * (priced, `available: false` in the pricing model, still in the library) is
 * absent from step II and from the spare swatches — never a disabled cell —
 * and a withdrawn or unknown material arriving from the URL, the bag or
 * storage counts as none chosen (a withdrawn spare metal falls back to the
 * build's). The one accessor for the offered metals is #offeredMaterials();
 * #material() stays the plain catalog lookup, for names.
 *
 * Spare discs (Review) come in any offered metal the store sells disc sets in:
 * a row of sphere swatches under "Spare discs, in <metal>" (one per offered
 * metal with a disc-set variant in any count, in the catalog's rank order; a
 * metal without a sphere shows its code) defaults to the build's metal; a
 * different choice is kept in `extraMaterial` (null = the build's metal) and
 * survives a change of the build's metal. The disc-set line then uses the
 * chosen metal's SKU, and the composition names it ("+8 CD"). Step I also
 * offers DISC SET ONLY, a link to the disc-set product for customers who want
 * no notebook.
 *
 * Sold out: a variant whose Shopify inventory has run out (`soldOut` in the
 * variant map) is still offered. Its option cell stays enabled with the
 * sold-out label as its note; on Review the sold-out block (cloned from the
 * section's <template data-sold-out-template>) replaces PLACE IN BAG and links
 * to the register-interest page with the composition code. A sold-out disc set
 * keeps its count cell the same way: choosing it prices the build and names
 * the discs as usual, and the block then stands in for the button; only a
 * count with no variant, or one withdrawn for another reason, is disabled and
 * resets to None. A metal whose every set has run out keeps its swatch, dimmed.
 * While no extra is chosen the extra line says which sets are spoken for
 * (derived from the variant map, so it survives a reload). Sold out wins over
 * the ordering gate.
 *
 * Beneath the dispatch note Review carries the REGISTER INTEREST link (Theme
 * settings → Store → Review link) to the register-interest page with the
 * composition code, whatever the ordering gate says. It is hidden while the
 * sold-out block is shown, since the block carries its own.
 */

import { getCart, addLines, changeLine, removeBuild, formatMoney } from "@theme/cart";
import {
  AS_MADE,
  UNAVAILABLE,
  decode,
  encode,
  findSku,
  fitColumns,
  linesFor,
  readConsultationData,
  registerInterestUrl,
  setSoldOutCode,
  soldOutBlock,
} from "@theme/composition";

const STORAGE_KEY = "dmw-consultation-v1";
const NUMERALS = ["I", "II", "III", "IV", "V", "VI", "VII"];
const STEP_NAMES = {
  format: "THE SIZE",
  material: "THE DISCS",
  leather: "THE LEATHER",
  paper: "THE PAPER",
  ruling: "THE RULING",
};
const QUESTIONS = {
  format: "What size suits your hand?",
  material: "Which metal should carry your pages?",
  leather: "Which leather will you wear in?",
  paper: "What will you write on?",
  ruling: "How should the page be ruled?",
};
const NEXT_LABELS = { material: "DISCS", leather: "LEATHER", paper: "PAPER", ruling: "RULING", review: "REVIEW" };
const SIZE_NOTES = { A7: "the pocket", A5: "the desk" };
const NOT_OFFERED = "Not offered";
const COPIED_FOR = 1500;

/** Creates an element; `text` sets textContent, `true` sets a boolean attribute, null/false skip. */
function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [name, value] of Object.entries(attrs)) {
    if (value == null || value === false) continue;
    if (name === "class") node.className = value;
    else if (name === "text") node.textContent = value;
    else node.setAttribute(name, value === true ? "" : String(value));
  }
  for (const child of [].concat(children)) {
    if (child) node.append(child);
  }
  return node;
}

/** Copies text to the clipboard, through the async API or the execCommand fallback; true on success. */
async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission refused or no secure context: fall back below.
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.setAttribute("aria-hidden", "true");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.append(area);
  area.select();
  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }
  area.remove();
  return copied;
}

/** "Perlinger Noblessa + Walpier Buttero" → "Perlinger Noblessa". */
function leatherName(leather) {
  return leather ? leather.name.split(" + ")[0] : "";
}

/** → "Lined in Walpier Buttero" (or the blurb when the name carries no lining). */
function leatherNote(leather) {
  const lining = leather.name.split(" + ")[1];
  return lining ? `Lined in ${lining}` : (leather.blurb ?? "");
}

/** → "Perlinger Noblessa · Buttero lined", as the bag renders it. */
function leatherLine(leather) {
  if (!leather) return "";
  const [name, lining] = leather.name.split(" + ");
  return lining ? `${name} · ${lining.split(" ").pop()} lined` : name;
}

function bindingTag(format) {
  return `${format.binding.toUpperCase()} BOUND`;
}

function bindingLabel(format) {
  return `${format.binding.charAt(0).toUpperCase()}${format.binding.slice(1)} bound`;
}

function setAriaDisabled(button, disabled) {
  if (disabled) button.setAttribute("aria-disabled", "true");
  else button.removeAttribute("aria-disabled");
}

/** "Spare discs in Zirconium are spoken for." for every count, else "Spare-disc sets of 6 and 8 in Zirconium are spoken for." */
function spokenForLine(soldOutCounts, allCounts, metal) {
  if (soldOutCounts.length === allCounts.length) return `Spare discs in ${metal} are spoken for.`;
  const words = soldOutCounts.map(String);
  const list = words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}` : words[0];
  return `Spare-disc sets of ${list} in ${metal} are spoken for.`;
}

/** The sphere asset URL per metal code from the section's <script data-spheres>; a metal without one is empty or absent. */
function readSpheres(root) {
  try {
    const map = JSON.parse(root.querySelector("script[data-spheres]")?.textContent ?? "{}");
    return map && typeof map === "object" ? map : {};
  } catch {
    return {};
  }
}

class MoonphaseConsultation extends HTMLElement {
  connectedCallback() {
    const data = readConsultationData(this);
    if (!data) return;
    this.catalog = data.catalog;
    this.blocks = data.blocks;
    this.variants = data.variants;
    this.skuIndex = data.skuIndex;
    this.soldOutLabel = data.soldOutLabel;
    this.soldOutTemplate = this.querySelector("template[data-sold-out-template]");
    this.spheres = readSpheres(this);
    this.stepRoot = this.querySelector("[data-step-root]");
    this.numeralsRoot = this.querySelector("[data-numerals]");
    this.refs = {};
    this.edit = null;
    this.busy = false;
    this.state = {
      format: null,
      material: null,
      leather: null,
      paper: null,
      ruling: null,
      note: "",
      extra: 0,
      extraMaterial: null,
      step: 0,
      editing: null,
      hover: null,
    };
    this.querySelector("[data-back]")?.addEventListener("click", this.#back);
    this.resizeObserver = new ResizeObserver(() => this.#fitGrids());
    this.resizeObserver.observe(this);
    this.#enter().then(() => this.#render(true));
  }

  disconnectedCallback() {
    this.querySelector("[data-back]")?.removeEventListener("click", this.#back);
    this.resizeObserver?.disconnect();
    clearTimeout(this.copyTimer);
  }

  #fitGrids() {
    for (const grid of this.querySelectorAll(".dmw-option-grid")) {
      grid.style.setProperty("--cols", fitColumns(grid.clientWidth, grid.children.length));
    }
  }

  /* Entry: ?edit rebuilds from the bag, otherwise storage; ?material / ?leather overwrite one choice,
     ?composition (not with ?edit) every field a decodable code names. */

  async #enter() {
    const params = new URLSearchParams(window.location.search);
    const editKey = params.get("edit");
    const config = (editKey && (await this.#configFromCart(editKey))) || this.#restore();
    const material = params.get("material");
    const leather = params.get("leather");
    if (this.#offeredMaterials().some((entry) => entry.code === material)) config.material = material;
    if (this.#leather(leather)) config.leather = leather;
    const composed = editKey ? null : decode(params.get("composition"), this.#composition()).state;
    if (composed) Object.assign(config, composed);
    Object.assign(this.state, this.#validate(config));
    this.#repairExtra();
    this.state.editing = this.edit?.key ?? null;
    // An update, or a composition, opens where the config runs out: Review when nothing is left to answer.
    this.state.step = this.state.editing || composed ? this.#maxReach() : 0;
  }

  async #configFromCart(key) {
    let cart;
    try {
      cart = await getCart();
    } catch {
      return null;
    }
    const line = cart.items?.find((item) => item.key === key);
    const sku = line?.sku ? this.catalog.skus[line.sku] : null;
    if (!sku || sku.type !== "notebook") return null;

    const properties = line.properties ?? {};
    const buildId = properties._build_id || null;
    const linked = buildId
      ? cart.items.find(
          (item) =>
            item.key !== key &&
            item.properties?._build_id === buildId &&
            this.catalog.skus[item.sku]?.type === "discset",
        )
      : null;
    const ruling = properties["Ruling note"]
      ? this.#blocksOf("ruling").find((block) => block.custom)
      : this.#blockByName("ruling", properties.Ruling);

    this.edit = { key, buildId, discKey: linked?.key ?? null };
    const linkedSku = linked ? this.catalog.skus[linked.sku] : null;
    return {
      format: sku.format,
      material: sku.material,
      leather: sku.leather,
      paper: this.#blockByName("paper", properties.Paper)?.id ?? null,
      ruling: ruling?.id ?? null,
      note: properties["Ruling note"] ?? "",
      extra: linkedSku ? linkedSku.discs : 0,
      extraMaterial: linkedSku && linkedSku.material !== sku.material ? linkedSku.material : null,
    };
  }

  #restore() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
      return saved && typeof saved === "object" ? saved : {};
    } catch {
      return {};
    }
  }

  #persist() {
    const { format, material, leather, paper, ruling, note, extra, extraMaterial } = this.state;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ format, material, leather, paper, ruling, note, extra, extraMaterial }),
      );
    } catch {
      // Storage unavailable: the consultation still works for this visit.
    }
  }

  /** A withdrawn or unknown material, from storage, the bag or a composition, is none chosen; so is a withdrawn spare metal. */
  #validate(config) {
    const material = this.#offeredMaterials().some((entry) => entry.code === config.material) ? config.material : null;
    const spareMetals = this.#spareMetals().map((entry) => entry.code);
    return {
      format: this.#format(config.format) ? config.format : null,
      material,
      leather: this.#leather(config.leather) ? config.leather : null,
      paper: this.#block("paper", config.paper) ? config.paper : null,
      ruling: this.#block("ruling", config.ruling) ? config.ruling : null,
      note: typeof config.note === "string" ? config.note : "",
      extra: this.#extraCounts().includes(config.extra) ? config.extra : 0,
      extraMaterial:
        config.extraMaterial !== material && spareMetals.includes(config.extraMaterial) ? config.extraMaterial : null,
    };
  }

  /** The context composition.js works from. */
  #composition() {
    return {
      catalog: this.catalog,
      blocks: this.blocks,
      skuIndex: this.skuIndex,
      variants: this.variants,
      soldOutLabel: this.soldOutLabel,
    };
  }

  /* Lookups. */

  #format(code) {
    return this.catalog.formats.find((format) => format.code === code) ?? null;
  }

  #material(code) {
    return this.catalog.materials.find((material) => material.code === code) ?? null;
  }

  /** The metals the store offers, in rank order: every catalog metal the pricing model has not withdrawn. */
  #offeredMaterials() {
    return this.catalog.materials.filter((material) => material.available !== false);
  }

  #leather(code) {
    return this.catalog.leathers.find((leather) => leather.code === code) ?? null;
  }

  #blocksOf(type) {
    return this.blocks.filter((block) => block.type === type);
  }

  #block(type, id) {
    return this.blocks.find((block) => block.type === type && block.id === id) ?? null;
  }

  #blockByName(type, name) {
    return this.blocks.find((block) => block.type === type && block.name === name) ?? null;
  }

  #extraCounts() {
    return [0, ...this.catalog.discSets.map((set) => set.discs)];
  }

  /** The metals spare discs come in, in rank order: the offered ones with a disc-set variant in any count (every offered metal until the product is imported). */
  #spareMetals() {
    const counts = this.catalog.discSets.map((set) => set.discs);
    return this.#offeredMaterials().filter((material) =>
      counts.some((discs) => this.#offer("discset", { discs, material: material.code })),
    );
  }

  /** The metal of the spare discs: the chosen one, else the build's. */
  #spareMetal() {
    return this.state.extraMaterial ?? this.state.material;
  }

  /** The leather to price a format with: the chosen one when it fits, else the first that does. */
  #leatherFor(formatCode) {
    const fits = (leather) => leather.formats.includes(formatCode);
    const current = this.#leather(this.state.leather);
    const leather = current && fits(current) ? current : this.catalog.leathers.find(fits);
    return leather?.code ?? null;
  }

  /* Steps. */

  get steps() {
    const paper = this.#block("paper", this.state.paper);
    return ["format", "material", "leather", "paper", ...(paper?.special ? [] : ["ruling"]), "review"];
  }

  get currentKey() {
    const steps = this.steps;
    return steps[Math.min(this.state.step, steps.length - 1)];
  }

  #needsNote() {
    return Boolean(this.#block("ruling", this.state.ruling)?.custom);
  }

  #completed(key) {
    if (!this.state[key]) return false;
    if (key === "ruling" && this.#needsNote()) return this.state.note.trim() !== "";
    return true;
  }

  /** The furthest step the numerals may jump to: the first one not yet completed. */
  #maxReach() {
    const steps = this.steps;
    let index = 0;
    while (index < steps.length - 1 && this.#completed(steps[index])) index += 1;
    return index;
  }

  #canContinue() {
    const key = this.currentKey;
    return key !== "review" && this.#completed(key);
  }

  #goToStep(step) {
    const target = Math.max(0, Math.min(step, this.steps.length - 1));
    if (target === this.state.step) return;
    this.state.step = target;
    this.state.hover = null;
    this.#render(true);
    window.scrollTo({ top: 0 });
    this.stepRoot.querySelector("[data-focus]")?.focus({ preventScroll: true });
  }

  #next = () => {
    if (this.#canContinue()) this.#goToStep(this.state.step + 1);
  };

  #back = () => {
    if (this.state.step > 0) this.#goToStep(this.state.step - 1);
    else window.location.assign(this.dataset.homeUrl || window.Shopify?.routes?.root || "/");
  };

  /* Offers: price and availability per ARCHITECTURE §9. `soldOut` (variant map only) means offered but run out. */

  #offer(kind, spec) {
    const sku = findSku(this.skuIndex, { type: kind, ...spec });
    if (!sku) return null;
    const variants = this.variants[kind];
    if (Object.keys(variants).length > 0) {
      const variant = variants[sku];
      if (!variant) return null;
      return {
        sku,
        price: variant.price,
        currency: undefined,
        available: Boolean(variant.available),
        soldOut: Boolean(variant.soldOut),
        variant,
      };
    }
    const entry = this.catalog.skus[sku];
    return {
      sku,
      price: Math.round(entry.price * 100),
      currency: this.catalog.meta.currency || "USD",
      available: Boolean(entry.available),
      soldOut: false,
      variant: null,
    };
  }

  /** Available, or sold out: the customer may still compose it. */
  #offered(offer) {
    return Boolean(offer?.available || offer?.soldOut);
  }

  #notebookOffer(view = this.state) {
    const leather = view.leather ?? this.#leatherFor(view.format);
    return this.#offer("notebook", { format: view.format, leather, material: view.material });
  }

  #discOffer(discs = this.state.extra) {
    return discs ? this.#offer("discset", { discs, material: this.#spareMetal() }) : null;
  }

  #canAdd() {
    if (this.dataset.orderingEnabled !== "true") return false;
    if (!this.#notebookOffer()?.variant?.available) return false;
    if (!this.state.extra) return true;
    return Boolean(this.#discOffer()?.variant?.available);
  }

  /* Options per step. */

  #options(key) {
    switch (key) {
      case "format":
        return this.catalog.formats.map((format) => this.#formatOption(format));
      case "material":
        return this.#offeredMaterials().map((material) => this.#materialOption(material));
      case "leather":
        return this.catalog.leathers
          .filter((leather) => leather.formats.includes(this.state.format))
          .map((leather) => this.#leatherOption(leather));
      case "paper":
        return this.#blocksOf("paper").map((block) => ({
          id: block.id,
          tag: block.special ? "SPECIAL" : "",
          name: block.name,
          note: block.note,
          enabled: true,
        }));
      case "ruling":
        return this.#blocksOf("ruling").map((block) => ({
          id: block.id,
          tag: "",
          name: block.name,
          note: block.note,
          enabled: true,
        }));
      default:
        return [];
    }
  }

  /** A format is sold out only when every offered metal in it is sold out or not offered in that size; disabled only when none is offered in it at all. */
  #formatOption(format) {
    const sizeNote = SIZE_NOTES[format.size] ? ` · ${SIZE_NOTES[format.size]}` : "";
    const leather = this.#leatherFor(format.code);
    const offers = this.#offeredMaterials().map((material) =>
      this.#offer("notebook", { format: format.code, leather, material: material.code }),
    );
    const available = format.available !== false && offers.some((offer) => offer?.available);
    const offered = format.available !== false && offers.some((offer) => this.#offered(offer));
    return {
      id: format.code,
      tag: bindingTag(format),
      name: format.size,
      note: available
        ? `${format.paper.w} × ${format.paper.h} mm${sizeNote} · ${format.discs} discs`
        : this.soldOutLabel,
      enabled: offered,
    };
  }

  /** An offered metal's cell: a sold-out one stays enabled with the sold-out label as its note. */
  #materialOption(material) {
    const offer = this.#offer("notebook", {
      format: this.state.format,
      leather: this.#leatherFor(this.state.format),
      material: material.code,
    });
    return {
      id: material.code,
      tag: "",
      name: material.name,
      note: offer?.available ? material.blurb : this.soldOutLabel,
      enabled: this.#offered(offer),
    };
  }

  #leatherOption(leather) {
    const offer = this.state.material
      ? this.#offer("notebook", { format: this.state.format, leather: leather.code, material: this.state.material })
      : null;
    return {
      id: leather.code,
      tag: "",
      name: leatherName(leather),
      note: leatherNote(leather),
      enabled: leather.available !== false && (!this.state.material || this.#offered(offer)),
    };
  }

  /** A step with exactly one available option is pre-selected (step III today). */
  #autoSelect(key) {
    if (key === "review" || this.state[key]) return;
    const enabled = this.#options(key).filter((option) => option.enabled);
    if (enabled.length === 1) this.state[key] = enabled[0].id;
  }

  #pick(key, id) {
    if (this.state[key] === id) return;
    const previous = this.state[key];
    this.state[key] = id;
    this.state.hover = null;
    if (key === "format") this.#repairLeather();
    if (key === "material") {
      // Spare discs follow the build's metal unless a different one was chosen on purpose.
      if (this.state.extraMaterial === previous || this.state.extraMaterial === id) this.state.extraMaterial = null;
      this.#repairExtra();
    }
    this.#update();
  }

  #repairLeather() {
    const leather = this.#leather(this.state.leather);
    if (leather && !leather.formats.includes(this.state.format)) this.state.leather = null;
  }

  /** A spare-disc count with no variant to buy or register for resets to None; a sold-out set stays chosen. */
  #repairExtra() {
    if (this.state.extra && !this.#offered(this.#discOffer())) this.state.extra = 0;
  }

  #pickExtra(count) {
    if (this.state.extra === count) return;
    this.state.extra = count;
    this.#update();
  }

  /** The build's own metal is the default, so choosing it clears the choice. */
  #pickExtraMaterial(code) {
    const next = code === this.state.material ? null : code;
    if (this.state.extraMaterial === next) return;
    this.state.extraMaterial = next;
    this.#repairExtra();
    this.#update();
  }

  /** The spare-disc counts whose set in the chosen metal has sold out, in cell order. */
  #soldOutExtras() {
    return this.#extraCounts().filter((count) => count && this.#discOffer(count)?.soldOut);
  }

  #setHover(key, id) {
    const next = key ? { key, id } : null;
    if (this.state.hover?.key === next?.key && this.state.hover?.id === next?.id) return;
    this.state.hover = next;
    this.#updateRenderSlot(false);
  }

  /* Rendering. A step change re-creates the step root so .dmw-enter replays; changes within a step patch in place. */

  #render(replay) {
    this.#autoSelect(this.currentKey);
    this.#persist();
    this.#renderNumerals();
    if (!replay) {
      if (this.currentKey === "review") this.#patchReview();
      else this.#updateQuestion();
      return;
    }
    const root = this.currentKey === "review" ? this.#buildReview() : this.#buildQuestion(this.currentKey);
    root.classList.add("dmw-enter");
    root.dataset.stepRoot = "";
    this.stepRoot.replaceWith(root);
    this.stepRoot = root;
    this.#fitGrids();
  }

  #update() {
    this.#render(false);
  }

  #renderNumerals() {
    const steps = this.steps;
    const reach = this.#maxReach();
    if (this.numeralsRoot.children.length !== steps.length) {
      this.numeralsRoot.replaceChildren(...steps.map((_, index) => this.#buildNumeral(index)));
    }
    [...this.numeralsRoot.children].forEach((button, index) => {
      const current = index === this.state.step;
      button.classList.toggle("is-current", current);
      button.disabled = index > reach;
      button.setAttribute("aria-label", `${NUMERALS[index]} · ${STEP_NAMES[steps[index]] ?? "REVIEW"}`);
      if (current) button.setAttribute("aria-current", "step");
      else button.removeAttribute("aria-current");
    });
  }

  #buildNumeral(index) {
    const button = el("button", { type: "button", class: "consultation__numeral", text: NUMERALS[index] });
    button.addEventListener("click", () => this.#goToStep(index));
    return button;
  }

  /* Render slot: the live build, from the state overlaid with the hovered option. */

  #view() {
    const view = { ...this.state };
    if (this.state.hover) view[this.state.hover.key] = this.state.hover.id;
    return view;
  }

  #renderLabel(view, prefix) {
    const parts = [
      this.#format(view.format)?.size ?? "A5",
      this.#material(view.material)?.name,
      leatherName(this.#leather(view.leather)),
      this.#block("paper", view.paper)?.name,
    ];
    return `${prefix} · ${parts.filter(Boolean).join(" · ").toUpperCase()}`;
  }

  /** The image of the exact variant, else of any variant in the same format, else null. */
  #imageFor(view) {
    if (!view.format) return null;
    const variants = this.variants.notebook;
    const exact = this.#notebookOffer(view)?.sku;
    if (exact && variants[exact]?.image) return variants[exact].image;
    const match = Object.entries(variants).find(
      ([sku, variant]) => variant.image && this.catalog.skus[sku]?.format === view.format,
    );
    return match ? match[1].image : null;
  }

  #buildRenderSlot(final) {
    const view = this.#view();
    const label = this.#renderLabel(view, final ? "FINAL RENDER" : "RENDER");
    const image = this.#imageFor(view);
    const className = final ? "consultation__render consultation__render--final" : "consultation__render";
    if (image) return el("img", { class: className, src: image, alt: label });
    return el("div", { class: `dmw-placeholder ${className}`, role: "img", "aria-label": label, text: label });
  }

  #updateRenderSlot(final) {
    if (!this.refs.render) return;
    const slot = this.#buildRenderSlot(final);
    this.refs.render.replaceWith(slot);
    this.refs.render = slot;
  }

  /* Question steps. */

  #buildQuestion(key) {
    const refs = { render: this.#buildRenderSlot(false), options: new Map() };
    const grid = el("div", { class: "dmw-option-grid", role: "group", "aria-label": QUESTIONS[key] });
    for (const option of this.#options(key)) {
      const button = this.#buildOption(key, option);
      refs.options.set(option.id, { button, option });
      grid.append(button);
    }
    refs.noteBlock = key === "ruling" ? this.#buildNoteBlock() : null;
    refs.hint =
      key === "paper" ? el("p", { class: "consultation__hint", text: this.dataset.paperHint, hidden: true }) : null;
    refs.continue = el("button", { type: "button", class: "dmw-button" });
    refs.continue.addEventListener("click", this.#next);
    this.refs = refs;
    const discSetLink =
      key === "format" && this.dataset.discSetUrl
        ? el(
            "div",
            { class: "consultation__disc-set" },
            el("a", { class: "dmw-text-link", href: this.dataset.discSetUrl, text: "DISC SET ONLY" }),
          )
        : null;

    const heading = el("h2", {
      class: "dmw-fit__text",
      style: "font-size: min(40px, 6.2cqi)",
      tabindex: "-1",
      "data-focus": "",
      text: QUESTIONS[key],
    });
    const root = el("div", {}, [
      refs.render,
      el("div", { class: "consultation__question" }, [
        el("span", { class: "dmw-eyebrow", text: `${NUMERALS[this.state.step]} · ${STEP_NAMES[key]}` }),
        el("div", { class: "dmw-fit" }, heading),
      ]),
      grid,
      refs.noteBlock,
      refs.hint,
      el("div", { class: "consultation__continue" }, refs.continue),
      discSetLink,
    ]);
    this.#patchQuestion();
    return root;
  }

  #buildOption(key, option) {
    const button = el(
      "button",
      { type: "button", class: "dmw-option", "aria-pressed": "false", disabled: !option.enabled },
      [
        el("span", { class: "dmw-option__tag" }),
        el("span", { class: "dmw-option__name", text: option.name }),
        el("span", { class: "dmw-option__note", text: option.enabled ? option.note : NOT_OFFERED }),
      ],
    );
    const hover = () => this.#setHover(key, option.id);
    const leave = () => this.#setHover(null);
    button.addEventListener("click", () => this.#pick(key, option.id));
    button.addEventListener("mouseenter", hover);
    button.addEventListener("mouseleave", leave);
    button.addEventListener("focus", hover);
    button.addEventListener("blur", leave);
    return button;
  }

  #buildNoteBlock() {
    const id = "consultation-ruling-note";
    const textarea = el("textarea", {
      id,
      class: "consultation__textarea",
      rows: "3",
      placeholder: "e.g. 5 mm dot grid with a 20 mm left margin",
      "aria-describedby": `${id}-help`,
    });
    textarea.value = this.state.note;
    textarea.addEventListener("input", () => {
      this.state.note = textarea.value;
      this.#persist();
      this.#patchQuestion();
      this.#renderNumerals();
    });
    return el("div", { class: "consultation__note dmw-enter", style: "--dmw-enter-duration: 0.4s", hidden: true }, [
      el("label", { class: "dmw-eyebrow", for: id, text: "DESCRIBE YOUR RULING" }),
      textarea,
      el("span", {
        id: `${id}-help`,
        class: "dmw-note",
        text: "We will confirm the ruling with you by email before the paper is cut.",
      }),
    ]);
  }

  #patchQuestion() {
    const key = this.currentKey;
    for (const [id, { button, option }] of this.refs.options) {
      const pressed = this.state[key] === id;
      button.setAttribute("aria-pressed", String(pressed));
      button.firstElementChild.textContent = option.tag || (pressed ? "SELECTED" : "");
    }
    const nextLabel = NEXT_LABELS[this.steps[this.state.step + 1]];
    this.refs.continue.textContent = nextLabel ? `CONTINUE TO ${nextLabel}` : "CONTINUE";
    setAriaDisabled(this.refs.continue, !this.#canContinue());
    if (this.refs.noteBlock) this.refs.noteBlock.hidden = !this.#needsNote();
    if (this.refs.hint) this.refs.hint.hidden = !this.#block("paper", this.state.paper)?.special;
  }

  #updateQuestion() {
    this.#patchQuestion();
    this.#updateRenderSlot(false);
  }

  /* Review. */

  #specRows() {
    const format = this.#format(this.state.format);
    const paper = this.#block("paper", this.state.paper);
    const ruling = this.#block("ruling", this.state.ruling);
    let rulingValue = ruling?.name ?? "";
    if (paper?.special) rulingValue = AS_MADE;
    else if (ruling?.custom) rulingValue = `Custom — ${this.state.note.trim()}`;
    return [
      { key: "Size", value: format ? `${format.size} · ${bindingLabel(format)}` : "", step: "format" },
      { key: "Discs", value: this.#material(this.state.material)?.name ?? "", step: "material" },
      { key: "Leather", value: leatherLine(this.#leather(this.state.leather)), step: "leather" },
      { key: "Paper", value: paper?.name ?? "", step: "paper" },
      { key: "Ruling", value: rulingValue, step: paper?.special ? "paper" : "ruling" },
    ];
  }

  #buildSpecRow(row) {
    const adjust = el("button", {
      type: "button",
      class: "consultation__adjust",
      "aria-label": `Adjust ${row.key.toLowerCase()}`,
      text: "ADJUST",
    });
    adjust.addEventListener("click", () => this.#goToStep(this.steps.indexOf(row.step)));
    return el("div", { class: "dmw-spec__row" }, [
      el("span", { class: "dmw-spec__key", text: row.key }),
      el("span", { class: "dmw-spec__value", text: row.value }),
      adjust,
    ]);
  }

  #buildReview() {
    const counts = this.#extraCounts();
    const refs = { render: this.#buildRenderSlot(true), extras: new Map() };
    const control = el("div", {
      class: "dmw-segmented",
      role: "group",
      "aria-label": "Additional discs",
      style: `grid-template-columns: repeat(${counts.length}, minmax(0, 1fr))`,
    });
    for (const count of counts) {
      const cell = el("button", {
        type: "button",
        class: "dmw-segmented__cell",
        "aria-pressed": "false",
        text: count ? String(count) : "None",
      });
      cell.addEventListener("click", () => this.#pickExtra(count));
      refs.extras.set(count, cell);
      control.append(cell);
    }
    refs.swatches = new Map();
    const swatches = this.#buildSwatches(refs.swatches);
    refs.metalName = el("span", { "data-extra-metal-name": "" });
    refs.price = el("span", { class: "consultation__price-value" });
    refs.extraLine = el("span", { class: "consultation__extra-line" });
    refs.code = el("span", { class: "dmw-spec__value consultation__code", "aria-live": "polite" });
    refs.copy = el("button", {
      type: "button",
      class: "consultation__adjust",
      "aria-label": "Copy composition",
      text: "COPY",
    });
    refs.copy.addEventListener("click", this.#copyCode);
    refs.composition = el("div", { class: "dmw-spec__row consultation__composition" }, [
      el("span", { class: "dmw-spec__key", text: "Composition" }),
      refs.code,
      refs.copy,
    ]);
    refs.add = el("button", { type: "button", class: "dmw-button dmw-button--block consultation__add" });
    refs.add.addEventListener("click", this.#placeInBag);
    refs.disabledNote = el("span", { class: "consultation__after", text: this.dataset.disabledNote, hidden: true });
    refs.dispatchNote = this.dataset.dispatchNote
      ? el("span", { class: "consultation__after", text: this.dataset.dispatchNote })
      : null;
    refs.interest =
      this.dataset.soldOutUrl && this.dataset.reviewLinkLabel
        ? el("a", { class: "dmw-text-link consultation__interest", text: this.dataset.reviewLinkLabel })
        : null;
    refs.soldOut = soldOutBlock(this.soldOutTemplate, this.dataset.soldOutUrl, null);
    if (refs.soldOut) refs.soldOut.hidden = true;
    refs.error = el("span", { class: "dmw-field-error consultation__error", role: "alert" });
    this.refs = refs;

    const name = el("span", {
      class: "dmw-fit__text",
      style: "font-size: min(34px, 7.1cqi)",
      tabindex: "-1",
      "data-focus": "",
      text: this.dataset.title || this.catalog.name,
    });
    const summary = el("div", { class: "consultation__summary" }, [
      el("span", { class: "dmw-eyebrow", text: this.dataset.reviewEyebrow }),
      el("div", { class: "dmw-fit consultation__name" }, name),
      ...this.#specRows().map((row) => this.#buildSpecRow(row)),
      el("div", { class: "dmw-rule" }),
      el("div", { class: "consultation__extras" }, [
        el("span", { class: "dmw-eyebrow", text: this.dataset.extrasEyebrow }),
        el("span", { class: "consultation__extras-title" }, ["Spare discs, in ", refs.metalName]),
        swatches,
        control,
      ]),
      el("div", { "aria-live": "polite" }, [
        el("div", { class: "consultation__price" }, [
          el("span", { class: "consultation__price-key", text: "Price" }),
          refs.price,
        ]),
        refs.extraLine,
      ]),
      refs.composition,
      refs.add,
      refs.disabledNote,
      refs.soldOut,
      refs.dispatchNote,
      refs.interest,
      refs.error,
    ]);
    this.#patchReview();
    return el("div", { class: "consultation__review" }, [refs.render, summary]);
  }

  /** Updates Review in place: swatches, cells, price, extra line, composition, and the button or the sold-out block. */
  #patchReview() {
    const soldOutExtras = this.#soldOutExtras();
    for (const [count, cell] of this.refs.extras) {
      cell.setAttribute("aria-pressed", String(this.state.extra === count));
      // A sold-out count stays choosable, as a sold-out metal does on step II; a set with no variant to sell is not.
      cell.disabled = count !== 0 && !this.#offered(this.#discOffer(count));
      if (soldOutExtras.includes(count)) cell.title = this.soldOutLabel;
      else cell.removeAttribute("title");
    }
    const extra = this.state.extra;
    const notebook = this.#notebookOffer();
    const disc = this.#discOffer();
    const offered = this.#offered(notebook) && (!extra || this.#offered(disc));
    // The notebook or the chosen disc set has run out: still priced, registered for rather than bought.
    const soldOut = offered && Boolean(notebook.soldOut || disc?.soldOut);
    const spare = this.#spareMetal();
    const metal = this.#material(spare)?.name ?? "";
    this.refs.metalName.textContent = metal;
    for (const [code, swatch] of this.refs.swatches) {
      swatch.setAttribute("aria-pressed", String(code === spare));
      swatch.classList.toggle("is-spoken-for", this.#metalSpokenFor(code));
    }

    this.refs.price.textContent = offered ? formatMoney(notebook.price + (disc?.price ?? 0), notebook.currency) : "—";
    if (!offered) this.refs.extraLine.textContent = UNAVAILABLE;
    else if (extra)
      this.refs.extraLine.textContent = `Includes ${extra} additional ${metal} disc${extra > 1 ? "s" : ""}.`;
    else if (soldOutExtras.length) {
      const offeredCounts = this.#extraCounts().filter((count) => count && this.#offered(this.#discOffer(count)));
      this.refs.extraLine.textContent = spokenForLine(soldOutExtras, offeredCounts, metal);
    } else this.refs.extraLine.textContent = "";

    const code = encode(this.state, this.#composition());
    this.refs.code.textContent = code ?? "";
    this.refs.composition.hidden = !code;
    if (this.refs.interest) {
      this.refs.interest.setAttribute(
        "href",
        registerInterestUrl(this.dataset.soldOutUrl, code ?? notebook?.sku ?? ""),
      );
    }

    // Sold out replaces the button and its notes, whatever the ordering gate says. The block carries its own
    // register-interest link, so the Review link beneath the dispatch note goes with them.
    const canAdd = this.#canAdd();
    setAriaDisabled(this.refs.add, !canAdd);
    this.refs.add.textContent = this.state.editing ? this.dataset.editingLabel : this.dataset.addLabel;
    this.refs.add.hidden = soldOut;
    this.refs.disabledNote.hidden = soldOut || canAdd || !offered;
    if (this.refs.dispatchNote) this.refs.dispatchNote.hidden = soldOut;
    if (this.refs.interest) this.refs.interest.hidden = soldOut;
    if (this.refs.soldOut) {
      this.refs.soldOut.hidden = !soldOut;
      if (soldOut) setSoldOutCode(this.refs.soldOut, this.dataset.soldOutUrl, code ?? notebook.sku);
    }
  }

  /* The metal swatches: one button per metal the spare discs come in, showing its sphere (its code when it has
     none). A press goes through #pickExtraMaterial; #patchReview marks the chosen one and dims a spoken-for metal. */

  #buildSwatches(swatches) {
    const row = el("div", {
      class: "consultation__swatches",
      role: "group",
      "aria-label": "Metal for the spare discs",
    });
    for (const material of this.#spareMetals()) {
      const sphere = this.spheres[material.code];
      const face = sphere
        ? el(
            "span",
            { class: "consultation__swatch-frame" },
            el("img", { src: sphere, alt: "", width: "740", height: "540", loading: "lazy" }),
          )
        : el("span", { text: material.code });
      const swatch = el(
        "button",
        {
          type: "button",
          class: "consultation__swatch",
          "aria-pressed": "false",
          "data-material": material.code,
          "aria-label": material.name,
        },
        face,
      );
      swatch.addEventListener("click", () => this.#pickExtraMaterial(material.code));
      swatches.set(material.code, swatch);
      row.append(swatch);
    }
    return row;
  }

  /** True when every disc-set count in a metal has sold out: none can be bought and at least one is spoken for. */
  #metalSpokenFor(code) {
    const offers = this.catalog.discSets.map((set) => this.#offer("discset", { discs: set.discs, material: code }));
    return !offers.some((offer) => offer?.available) && offers.some((offer) => offer?.soldOut);
  }

  #copyCode = async () => {
    const { code, copy } = this.refs;
    if (!code.textContent || !(await copyText(code.textContent))) return;
    copy.textContent = "COPIED";
    copy.setAttribute("aria-label", "Copied");
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      copy.textContent = "COPY";
      copy.setAttribute("aria-label", "Copy composition");
    }, COPIED_FOR);
  };

  /* Adding: the lines come from composition.js (notebook line plus a disc-set line sharing its _build_id). */

  #placeInBag = async () => {
    if (this.busy || !this.#canAdd()) return;
    const { add, error } = this.refs;
    this.busy = true;
    add.setAttribute("aria-busy", "true");
    error.textContent = "";
    try {
      const lines = linesFor(this.state, this.#composition());
      if (lines.error) throw new Error(lines.error);
      if (this.edit) await this.#removeEdited();
      await addLines(lines.items);
      document.querySelector("bag-drawer")?.open(add);
    } catch (failure) {
      error.textContent =
        failure instanceof Error ? failure.message : "The bag could not be updated. Please try again.";
    } finally {
      this.busy = false;
      add.removeAttribute("aria-busy");
    }
  };

  /** Removes both lines of the build being edited, by build id from the drawer or by line key. */
  async #removeEdited() {
    const { key, buildId, discKey } = this.edit;
    const removed = buildId ? await removeBuild(buildId) : null;
    if (!removed) {
      await changeLine(key, 0);
      if (discKey) await changeLine(discKey, 0);
    }
    this.#finishEdit();
  }

  #finishEdit() {
    this.edit = null;
    this.state.editing = null;
    if (new URLSearchParams(window.location.search).has("edit")) {
      window.history.replaceState(null, "", this.dataset.productUrl || window.location.pathname);
    }
    if (this.currentKey === "review") this.#patchReview();
  }
}

if (!customElements.get("moonphase-consultation")) {
  customElements.define("moonphase-consultation", MoonphaseConsultation);
}
